import type { AxiosResponse } from 'axios'
import { useMutation, useQueryClient } from 'react-query'

import { QueryKeys } from '../../../enums/queryKeys'
import { updateZervantCustomDocumentTemplate } from '../query-api'
import type { ZervantTemplate } from '../types/templates/zervantTemplate'
import type { ZervantTemplateSettings } from '../types/templates/zervantTemplateSettings'

const DEFAULT_TEMPLATE_NAME = 'default'

export const useUpdateZervantDocumentTemplateSettings = () => {
  const queryClient = useQueryClient()

  const {
    mutate: update,
    isLoading: isProcessing,
    ...rest
  } = useMutation(
    (templateSettings: ZervantTemplateSettings) => {
      // The PUT replaces the whole template. Read the cache here, not from the render that built the settings.
      const templateQueryState = queryClient.getQueryState<AxiosResponse<ZervantTemplate | ''>>(
        QueryKeys.ZervantDocumentTemplateSettings,
      )

      if (!templateQueryState?.dataUpdatedAt) {
        throw new Error('Document template is not loaded')
      }

      // An organization without a saved template gets an empty response body.
      const cachedTemplate = templateQueryState.data?.data || undefined

      return updateZervantCustomDocumentTemplate({
        name: cachedTemplate?.name ?? DEFAULT_TEMPLATE_NAME,
        settings: {
          ...cachedTemplate?.settings,
          ...templateSettings,
        },
      })
    },
    {
      onSuccess: () => queryClient.invalidateQueries(QueryKeys.ZervantDocumentTemplateSettings),
    },
  )

  return { update, isProcessing, ...rest }
}
